import { useEffect, useState } from "react";
import { IonContent, IonHeader, IonModal, IonTitle, IonToolbar } from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { Button, Colors, Icon } from "swing-components";

import { MarkdownContent } from "~components";
import type { DistrictsAnswerType } from "~onboarding/utils";
import { GET } from "~utils";

const INITIAL_DISTRICTS_COUNT = 5;

type DistrictsAnswerProps = Omit<DistrictsAnswerType, "type"> & {
  /**
   * When the content is rendered, pass the `value` defined in the DistrictsAnswer type to the parent.
   */
  onChange: (value: string) => void;
  /**
   * Disables the editability of the answer.
   */
  isReadOnly?: boolean;
};

export function DistrictsAnswer(props: DistrictsAnswerProps) {
  const { markdownBefore, markdownAfter, isReadOnly, onChange, value } = props;
  const [isAllDistrictsModalOpen, setIsAllDistrictsModalOpen] = useState(false);

  const { data: districtNames } = useQuery({
    queryKey: ["districts"],
    queryFn: () => GET("/api/sub/district"),
    select: (data) => data.data,
  });

  useEffect(() => {
    if (isReadOnly === false) {
      onChange(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const names = districtNames ?? [];
  const visibleNames = names.slice(0, INITIAL_DISTRICTS_COUNT);
  const hasMoreThanFive = names.length > INITIAL_DISTRICTS_COUNT;

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {markdownBefore && (
        <MarkdownContent data-testid="markdown" body={markdownBefore} css={{ width: "100%" }} />
      )}
      <ul>
        {visibleNames.map((name) => (
          <li key={name}>{name}</li>
        ))}
      </ul>
      {hasMoreThanFive && (
        <>
          <Button
            type="secondary"
            status={isReadOnly ? "disabled" : "enabled"}
            onClick={() => setIsAllDistrictsModalOpen(true)}
          >
            {`Show all ${names.length} districts`}
          </Button>
          <IonModal
            isOpen={isAllDistrictsModalOpen}
            onDidDismiss={() => setIsAllDistrictsModalOpen(false)}
          >
            <IonHeader>
              <IonToolbar>
                <IonTitle>Districts</IonTitle>
                <button
                  type="button"
                  slot="end"
                  onClick={() => setIsAllDistrictsModalOpen(false)}
                  css={{
                    background: "transparent",
                    border: "none",
                    padding: 8,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  aria-label="Close"
                >
                  <Icon name="Close" color={Colors.blue500} />
                </button>
              </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
              <ul css={{ paddingInlineStart: 20, margin: 0 }}>
                {names.map((name) => (
                  <li key={name} css={{ marginBottom: 8 }}>
                    {name}
                  </li>
                ))}
              </ul>
            </IonContent>
          </IonModal>
        </>
      )}
      {markdownAfter && (
        <MarkdownContent data-testid="markdown" body={markdownAfter} css={{ width: "100%" }} />
      )}
    </div>
  );
}
